import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/library/select";
import { CheckCircle2, Info, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { getLeadTracks, leadsEndpoints } from "@/config/endpoints";
import {
  registerTrackRequirementPrompt,
  type TrackRequirementFailure,
  type TrackRequirementPrompt,
} from "@/lib/trackRequirementGate";
import { queryClient } from "@/lib/queryClient";
import { invalidateTrackQueries } from "@/features/stage/api/invalidateTrackQueries";
import useTracksPipeline from "@/features/stage/api/useTracksPipeline";
import { useLeadMutations } from "@/features/leads/api/useLeadMutations";
import { useDocumentMutaion } from "@/hooks/api/useDocmentMutation";
import useUsers from "@/features/users/api/useUsers";
import useCustomInputs from "@/features/customField/api/useCustomInputs";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";
import { normalizeCustomInputScopesForForm } from "@/utils/getFromCustom";

/**
 * Replace (or add) ONE custom field's entry while carrying every other scope
 * through untouched — the same rule as the drawer's inline edit
 * (`useInlineFieldEdit.mergeCustomScope`): the server rewrites the whole
 * scope list from the payload, so a partial list would wipe the rest.
 * Copied rather than imported: that hook drags the drawer's form contexts
 * into this app-wide host.
 */
function mergeCustomScope(
  base: any[],
  customInputId: number,
  patch: { optionIds?: string[]; textValue?: string },
): any[] {
  const id = Number(customInputId);
  let found = false;
  const merged = (base ?? []).map((scope: any) => {
    if (Number(scope?.customInputId) !== id) return scope;
    found = true;
    return { ...scope, ...patch };
  });
  if (!found) merged.push({ customInputId: id, ...patch });
  return merged;
}

/**
 * Globally mounted "This track status needs a few things first" dialog (see
 * AppProvider) — the track twin of `TransitionNoteHost`.
 *
 * A track status change the server refuses on a TRACK REQUIREMENT comes back
 * as 400 + `data.trackRequirement` + `data.requirements`. The track mutation
 * holds its promise, opens this dialog through `trackRequirementGate`, and
 * replays the change when the person continues. Every missing item gets a
 * row with the control that adds it, written through the drawer's own
 * paths (lead update, document upload, assignment, track add), so nothing
 * here is a second writer:
 *
 *   Fields     an input for the lead field (phone, email, name, …)
 *   Documents  a file picker that uploads under the required name
 *   Custom     the field's option list, or a text box for a text field
 *   Roles      a person of that role to assign
 *   User       a one-click assign of that person
 *   Tracks     add the lead to the required track at a chosen status
 *   Modules / DealCount   a hint (those are added from the lead's Overview)
 *
 * The popup never decides whether the gate is now met — the server does, on
 * the replay. When every row is added the change is replayed by itself.
 */

const FIELD_LABELS: Record<string, string> = {
  phone: "Phone",
  email: "Email",
  name: "Name",
  whatsapp: "WhatsApp",
  location: "Location",
};

const TYPE_LABELS: Record<string, string> = {
  Fields: "Field",
  Documents: "Document",
  Custom: "Custom field",
  Roles: "Role",
  User: "Person",
  Modules: "Module",
  Tracks: "Track",
  DealCount: "Deals",
};

type RowProps = {
  failure: TrackRequirementFailure;
  prompt: TrackRequirementPrompt;
  lead: any;
  done: boolean;
  onDone: () => void;
};

function RowShell({
  failure,
  done,
  children,
}: {
  failure: TrackRequirementFailure;
  done: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-xl border px-3 py-2.5 space-y-2 ${
        done ? "border-[#bfe0d0] bg-[#eaf6f0]" : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 shrink-0">
              {TYPE_LABELS[failure.type] ?? failure.type}
            </span>
            <span className="text-sm font-medium text-[#051912] truncate">
              {failure.type === "Fields"
                ? FIELD_LABELS[failure.referenceKey] ?? failure.referenceKey
                : failure.type === "DealCount"
                  ? `At least ${failure.referenceKey} deal${failure.referenceKey === "1" ? "" : "s"}`
                  : failure.referenceKey}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{failure.errorMessage}</p>
        </div>
        {done && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-[#156548] shrink-0">
            <CheckCircle2 className="w-4 h-4" /> Added
          </span>
        )}
      </div>
      {!done && children}
    </div>
  );
}

function FieldRow({ failure, prompt, lead, done, onDone }: RowProps) {
  const [value, setValue] = useState("");
  const { updateLead } = useLeadMutations();
  const key = failure.referenceKey;
  const save = async () => {
    const v = value.trim();
    if (!v) return;
    const data: Record<string, unknown> = { [key]: v };
    // The drawer sends the country code with the number; keep the lead's.
    if (key === "phone" && lead?.phoneCountryCode) data.phoneCountryCode = lead.phoneCountryCode;
    try {
      await updateLead.mutateAsync({ id: prompt.leadId, data });
      onDone();
    } catch {
      /* the mutation's own toast */
    }
  };
  return (
    <RowShell failure={failure} done={done}>
      <div className="flex items-center gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={`Enter ${(FIELD_LABELS[key] ?? key).toLowerCase()}`}
          className="border-gray-200 h-9"
          type={key === "email" ? "email" : "text"}
        />
        <Button
          variant="primary"
          size="sm"
          disabled={!value.trim() || updateLead.isPending}
          onClick={save}
        >
          {updateLead.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
        </Button>
      </div>
    </RowShell>
  );
}

function DocumentRow({ failure, prompt, lead, done, onDone }: RowProps) {
  const { uploadFile } = useDocumentMutaion();
  const inputRef = useRef<HTMLInputElement>(null);
  const onPick = async (file: File | undefined) => {
    if (!file) return;
    try {
      await uploadFile.mutateAsync({
        file,
        // Saved under the REQUIRED name, which is what the gate matches on.
        name: failure.referenceKey,
        attachedTo: "leads",
        attachedScope: String(prompt.leadId),
        category: lead?.stage?.name ?? "Documents",
        childId: prompt.entityType === "child" ? prompt.childId : null,
      });
      onDone();
    } catch (e: any) {
      toast.error(e?.clientMessage || e?.response?.data?.message || "Upload failed");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <RowShell failure={failure} done={done}>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => void onPick(e.target.files?.[0])}
      />
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5"
        disabled={uploadFile.isPending}
        onClick={() => inputRef.current?.click()}
      >
        {uploadFile.isPending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Upload className="w-3.5 h-3.5" />
        )}
        Upload {failure.referenceKey}
      </Button>
    </RowShell>
  );
}

function CustomRow({ failure, prompt, lead, done, onDone }: RowProps) {
  const { connections: customInputs = [] } = useCustomInputs();
  const { updateLead } = useLeadMutations();
  const [optionId, setOptionId] = useState("");
  const [text, setText] = useState("");
  const field = useMemo(
    () => (customInputs as any[]).find((c: any) => Number(c.id) === Number(failure.referenceId)),
    [customInputs, failure.referenceId],
  );
  const options: { id: number; value: string }[] = useMemo(
    () =>
      ((field?.options ?? field?.customInputItems ?? []) as any[])
        .filter((o: any) => o?.id != null && o?.value != null && String(o.value).trim() !== "")
        .map((o: any) => ({ id: Number(o.id), value: String(o.value) })),
    [field],
  );
  const isSelector = field?.type === "SELECTOR" && options.length > 0;
  const label = field ? getCfDisplayName(field) : failure.referenceKey;
  const canSave = isSelector ? !!optionId : !!text.trim();
  const save = async () => {
    if (!field || !lead) return;
    const patch = isSelector ? { optionIds: [optionId] } : { textValue: text.trim() };
    try {
      // Same shape as the drawer's inline edit: the WHOLE scope list with
      // this one field replaced, because the server rewrites the list.
      const base = normalizeCustomInputScopesForForm(lead?.customInputScopes);
      await updateLead.mutateAsync({
        id: prompt.leadId,
        data: { customInputScopes: mergeCustomScope(base, Number(field.id), patch) },
      });
      onDone();
    } catch {
      /* the mutation's own toast */
    }
  };
  return (
    <RowShell failure={failure} done={done}>
      {!field ? (
        <p className="text-xs text-gray-500">
          Fill "{failure.referenceKey}" on the lead's Overview, then continue.
        </p>
      ) : (
        <div className="flex items-center gap-2">
          {isSelector ? (
            <Select value={optionId} onValueChange={setOptionId}>
              <SelectTrigger className="h-9 text-gray-700 bg-white border border-gray-200 rounded-md focus:ring-0 focus:ring-offset-0">
                <SelectValue placeholder={`Pick ${label}`} />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.id} value={String(o.id)}>
                    {o.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Enter ${label}`}
              className="border-gray-200 h-9"
            />
          )}
          <Button
            variant="primary"
            size="sm"
            disabled={!canSave || !lead || updateLead.isPending}
            onClick={save}
          >
            {updateLead.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
          </Button>
        </div>
      )}
    </RowShell>
  );
}

function AssignRow({ failure, prompt, done, onDone }: RowProps) {
  const { users = [] } = useUsers();
  const { assignLeads } = useLeadMutations();
  const [userId, setUserId] = useState("");
  const byRole = failure.type === "Roles";
  const candidates = useMemo(
    () =>
      ((users as any[]) ?? [])
        .filter((u: any) => u?.id != null && u?.isActive !== false)
        .filter((u: any) =>
          byRole
            ? Number(u.roleId) === Number(failure.referenceId)
            : Number(u.id) === Number(failure.referenceId),
        )
        .map((u: any) => ({ id: Number(u.id), name: String(u.name ?? u.email ?? u.id) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [users, byRole, failure.referenceId],
  );
  const pick = byRole ? userId : candidates[0] ? String(candidates[0].id) : "";
  const assign = async () => {
    if (!pick) return;
    try {
      await assignLeads.mutateAsync({
        selectedLeadIds: [prompt.leadId],
        selectedUser: Number(pick),
        level: "lead",
      });
      onDone();
    } catch {
      /* the mutation's own toast */
    }
  };
  return (
    <RowShell failure={failure} done={done}>
      {candidates.length === 0 ? (
        <p className="text-xs text-gray-500">
          {byRole
            ? `No active user holds the "${failure.referenceKey}" role.`
            : `${failure.referenceKey} is not an active user.`}
        </p>
      ) : (
        <div className="flex items-center gap-2">
          {byRole && (
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="h-9 text-gray-700 bg-white border border-gray-200 rounded-md focus:ring-0 focus:ring-offset-0">
                <SelectValue placeholder={`Pick a ${failure.referenceKey}`} />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((u) => (
                  <SelectItem key={u.id} value={String(u.id)}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="primary"
            size="sm"
            disabled={!pick || assignLeads.isPending}
            onClick={assign}
          >
            {assignLeads.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : byRole ? (
              "Assign"
            ) : (
              `Assign ${candidates[0]?.name ?? ""}`
            )}
          </Button>
        </div>
      )}
    </RowShell>
  );
}

function TrackRow({ failure, prompt, done, onDone }: RowProps) {
  const { tracks: allTracks = [] } = useTracksPipeline();
  const [subTrackId, setSubTrackId] = useState("");
  const [saving, setSaving] = useState(false);
  const track = useMemo(
    () => ((allTracks as any[]) ?? []).find((t: any) => Number(t.id) === Number(failure.referenceId)),
    [allTracks, failure.referenceId],
  );
  const statuses: { id: number; name: string; isDefault: boolean }[] = useMemo(
    () =>
      ((track?.subTracks ?? []) as any[]).map((s: any) => ({
        id: Number(s.id),
        name: String(s.name),
        isDefault: !!s.isDefault,
      })),
    [track],
  );
  useEffect(() => {
    if (!subTrackId && statuses.length) {
      setSubTrackId(String((statuses.find((s) => s.isDefault) ?? statuses[0]).id));
    }
  }, [statuses, subTrackId]);
  const add = async () => {
    if (!track || !subTrackId) return;
    setSaving(true);
    try {
      // The drawer's own add. A deal-based track goes on the deal when the
      // refused change was on one; a lead-based track always on the lead.
      await axiosInstance.post(getLeadTracks, {
        leadId: prompt.leadId,
        trackId: Number(track.id),
        subTrackId: Number(subTrackId),
        ...(track.grain === "deal" && prompt.childId ? { childId: prompt.childId } : {}),
      });
      queryClient.invalidateQueries({ queryKey: ["entity-tracks"] });
      invalidateTrackQueries(queryClient);
      toast.success(`Added to ${track.name}`);
      onDone();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || `Could not add ${track?.name ?? "the track"}`);
    } finally {
      setSaving(false);
    }
  };
  return (
    <RowShell failure={failure} done={done}>
      {!track ? (
        <p className="text-xs text-gray-500">
          Add the lead to "{failure.referenceKey}" from its Tracks tab, then continue.
        </p>
      ) : (
        <div className="flex items-center gap-2">
          <Select value={subTrackId} onValueChange={setSubTrackId}>
            <SelectTrigger className="h-9 text-gray-700 bg-white border border-gray-200 rounded-md focus:ring-0 focus:ring-offset-0">
              <SelectValue placeholder="Track status" />
            </SelectTrigger>
            <SelectContent>
              {statuses.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="primary" size="sm" disabled={!subTrackId || saving} onClick={add}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Add to ${track.name}`}
          </Button>
        </div>
      )}
    </RowShell>
  );
}

function HintRow({ failure, done }: RowProps) {
  const hint =
    failure.type === "Modules"
      ? `Attach a ${failure.referenceKey} to the lead from its Overview, then continue.`
      : failure.type === "DealCount"
        ? `Add the deal${failure.referenceKey === "1" ? "" : "s"} from the lead's Deals, then continue.`
        : "Add this on the lead, then continue.";
  return (
    <RowShell failure={failure} done={done}>
      <p className="inline-flex items-start gap-1.5 text-xs text-gray-500">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-500" />
        {hint}
      </p>
    </RowShell>
  );
}

function RequirementRow(props: RowProps) {
  switch (props.failure.type) {
    case "Fields":
      return <FieldRow {...props} />;
    case "Documents":
      return <DocumentRow {...props} />;
    case "Custom":
      return <CustomRow {...props} />;
    case "Roles":
    case "User":
      return <AssignRow {...props} />;
    case "Tracks":
      return <TrackRow {...props} />;
    default:
      return <HintRow {...props} />;
  }
}

/** Rows that carry a control; the others are hints the person acts on elsewhere. */
const ACTIONABLE = new Set(["Fields", "Documents", "Custom", "Roles", "User", "Tracks"]);

const HOST_LEAD_KEY = "track-requirement-lead";

/**
 * The lead's record for the popup (custom-field scope list, phone code,
 * stage name). Its own small query, NOT the drawer's `useCurrentLead`: that
 * hook reads the router's location for the anchor filter, and this host is
 * mounted app-wide outside the router — using it blanked the whole app
 * (found in the local browser pass, 2026-11-02).
 */
function useHostLead(leadId: number | null) {
  const query = useQuery({
    queryKey: [HOST_LEAD_KEY, leadId],
    queryFn: async () => {
      const res = await axiosInstance.get(`${leadsEndpoints}/${leadId}`);
      return res.data?.lead ?? null;
    },
    enabled: leadId != null && Number.isFinite(leadId) && leadId > 0,
    staleTime: 0,
  });
  return { lead: query.data as any, refetch: query.refetch };
}

export default function TrackRequirementHost() {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState<TrackRequirementPrompt | null>(null);
  const [done, setDone] = useState<Record<number, boolean>>({});
  const resolveRef = useRef<((ok: boolean) => void) | null>(null);

  const settle = useCallback((value: boolean) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setOpen(false);
  }, []);

  useEffect(() => {
    registerTrackRequirementPrompt(
      (p) =>
        new Promise<boolean>((resolve) => {
          resolveRef.current = resolve;
          setPrompt(p);
          setDone({});
          setOpen(true);
        }),
    );
    return () => registerTrackRequirementPrompt(null);
  }, []);

  // The lead's record, for the custom-field scope list and the phone code.
  const { lead, refetch: refetchLead } = useHostLead(open && prompt ? prompt.leadId : null);
  const { tracks: allTracks = [] } = useTracksPipeline();
  const target = useMemo(() => {
    if (!prompt) return null;
    const track = ((allTracks as any[]) ?? []).find((t: any) => Number(t.id) === prompt.trackId);
    const status = ((track?.subTracks ?? []) as any[]).find(
      (s: any) => Number(s.id) === prompt.subTrackId,
    );
    return track ? { track: String(track.name), status: status ? String(status.name) : null } : null;
  }, [allTracks, prompt]);

  const failures = prompt?.failures ?? [];
  const actionable = failures.filter((f) => ACTIONABLE.has(f.type)).length;
  const added = failures.filter((f, i) => ACTIONABLE.has(f.type) && done[i]).length;
  const allAdded = actionable > 0 && added === actionable && actionable === failures.length;

  // Every missing item added → replay the change without another click.
  useEffect(() => {
    if (open && allAdded) settle(true);
  }, [open, allAdded, settle]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && settle(false)}>
      <DialogContent className="sm:max-w-[520px] rounded-xl max-h-[88vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <p className="text-base font-semibold text-[#051912]">
            {target?.status
              ? `"${target.status}" needs a few things first`
              : "This track status needs a few things first"}
          </p>
          <p className="text-xs text-gray-500">
            {target
              ? `Add what is missing and the ${target.track} track will be updated.`
              : "Add what is missing and the track will be updated."}
          </p>
          <Separator />
        </DialogHeader>

        <div className="space-y-2 overflow-y-auto flex-1 pr-1">
          {prompt &&
            failures.map((f, i) => (
              <RequirementRow
                key={`${f.type}-${f.referenceKey}-${i}`}
                failure={f}
                prompt={prompt}
                lead={lead}
                done={!!done[i]}
                onDone={() => {
                  setDone((d) => ({ ...d, [i]: true }));
                  // The next row's custom-field merge must start from the
                  // record as it is now.
                  void refetchLead();
                }}
              />
            ))}
        </div>

        <DialogFooter className="mt-2 flex justify-end gap-2 shrink-0">
          <Button variant="cancel" onClick={() => settle(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => settle(true)}>
            {added > 0 ? "Continue" : "I've added it, continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
